import { formatMoney } from '../lib/format.js'

// "AI Restaurant Manager has saved you $X" tile.
// Lives above the running-total card — the headline value-prop indicator
// for the whole product. Owner sees this number grow week over week.
//
// Sources of dollar_amount come from public.savings_events, summed by
// the vw_savings_summary view. The methodology line tells the owner
// exactly which baseline produced the most recent number — keeping the
// verifiable-vs-potential rule (Instructions Claude.md §7a) auditable.
//
// onReviewClick (optional): callback invoked when the owner taps the
// "X pending review" pill. Wires up to OwnerAttestationModal.
//
// onBreakdownClick (optional): callback invoked when the owner taps
// the tile body. Wires up to SavingsBreakdownSheet (read-only details).
export default function LifetimeSavings({ summary, dailyTarget, onReviewClick, onBreakdownClick }) {
  const lifetime       = Number(summary?.lifetime_total ?? 0)
  const today          = Number(summary?.today_total    ?? 0)
  const eventCount     = Number(summary?.event_count    ?? 0)
  const confirmedCount = Number(summary?.confirmed_count ?? 0)
  const lastSource     = summary?.last_event_source

  // Day-zero state: no events yet. Shouldn't normally happen now that
  // daily_under_target fires from day 1, but cover the edge case
  // (e.g. fresh DB before the first nightly cron run).
  if (eventCount === 0) {
    return (
      <section
        className="mx-4 mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4"
        aria-label="Savings tracker"
      >
        <div className="text-xs font-medium text-emerald-700">
          Savings tracker
        </div>
        <div className="mt-1 text-sm text-black/70">
          Your first savings event lands tonight after kitchen close.
        </div>
      </section>
    )
  }

  // Methodology line — tells the owner exactly how this number was earned.
  let methodology
  switch (lastSource) {
    case 'daily_under_target':
      methodology = `Under your ${formatMoney(dailyTarget)} daily target`
      break
    case 'waste_avoided':
      methodology = `Under your 7-day rolling average`
      break
    case 'manual_attestation':
      methodology = `Verified by you`
      break
    default:
      methodology = `Computed daily`
  }

  // Three states for the trailing badge:
  //   - allVerified: every event has been owner-confirmed
  //   - hasPending: at least one unconfirmed event needs review
  //   - mixed: some confirmed, some not (rare — happens between attestations)
  const pendingCount = eventCount - confirmedCount
  const allVerified  = pendingCount === 0 && confirmedCount > 0
  const hasPending   = pendingCount > 0

  // Make the whole tile a tappable surface so any tap (except the
  // inline "review" pill below) opens the breakdown sheet. Using
  // role="button" on the section so the inline review button can be
  // a real <button> without nesting button elements.
  const tileTappable = !!onBreakdownClick
  const tileProps = tileTappable
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: onBreakdownClick,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onBreakdownClick()
          }
        },
        'aria-label': 'View savings breakdown',
      }
    : {}

  return (
    <section
      {...tileProps}
      className={`mx-4 mt-4 rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm ${
        tileTappable
          ? 'cursor-pointer transition hover:bg-emerald-50/40 active:bg-emerald-50/60 focus:outline-none focus:ring-2 focus:ring-emerald-600/30'
          : ''
      }`}
    >
      <div className="text-xs font-medium text-emerald-700">
        AI Restaurant Manager has saved you
      </div>

      <div className="mt-1 flex items-baseline gap-3">
        <span className="text-4xl font-medium tabular-nums text-emerald-700">
          {formatMoney(lifetime)}
        </span>
        <span className="text-sm text-black/60">Lifetime</span>
        {allVerified && (
          <span
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
            aria-label="Verified by owner"
            title="Verified by owner"
          >
            ✓ Verified
          </span>
        )}
      </div>

      {today > 0 && (
        <div className="mt-1 text-sm">
          <span className="text-emerald-700 font-medium">
            ↑ <span className="tabular-nums">{formatMoney(today)}</span>
          </span>
          <span className="ml-1 text-black/60">today</span>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-1 text-xs text-black/55">
        <span>{methodology}</span>

        {hasPending && onReviewClick && (
          <>
            <span>·</span>
            <button
              type="button"
              onClick={(e) => {
                // Stop the click from bubbling up to the tile's
                // breakdown handler — review is its own action.
                e.stopPropagation()
                onReviewClick()
              }}
              className="min-h-[24px] rounded px-1 font-medium text-emerald-700 hover:bg-emerald-50 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-600/30"
            >
              {pendingCount} pending review →
            </button>
          </>
        )}

        {allVerified && (
          <>
            <span>·</span>
            <span className="text-emerald-700">verified by owner</span>
          </>
        )}

        {/* Tap-affordance chevron, right-aligned. Subtle but clear. */}
        {tileTappable && (
          <span className="ml-auto pl-2 text-emerald-700/70" aria-hidden="true">
            View breakdown →
          </span>
        )}
      </div>
    </section>
  )
}
